import {
  FormatListNumbered,
  InfoOutlined,
  SignalCellularAlt,
} from '@mui/icons-material';
import { Box, Skeleton, Stack, styled, Switch, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useWindowWidth } from '@react-hook/window-size';
import Bowser from 'bowser';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighChartsMore from 'highcharts/highcharts-more';
import React from 'react';
import { ChartRanking, homeStyles } from '..';
import { AnimateIn } from '../../../components/animate-in';
import { uicolors } from '../../../theme';
import { toCurrency } from '../../../utils/formatter';

const detectMobile = (): boolean => {
  const parser = Bowser.getParser(window.navigator.userAgent);
  return parser.getPlatform().type === 'mobile';
};

HighChartsMore(Highcharts);

export function Chart(props: {
  AgtNo: string;
  Title: string;
  data: ChartRanking[];
  ToolTip?: string | null;
  isCurrency?: boolean;
  mobileOpen?: string | null;
  cantView?: boolean;
}) {
  // Props
  const { AgtNo, Title, data, isCurrency, mobileOpen, cantView } = props;
  // Hooks
  const width = useWindowWidth();
  const isMobile = detectMobile();
  // State
  const [toggle, setToggle] = React.useState(false);

  React.useEffect(() => {
    setToggle(mobileOpen === 'list' ? true : false);
  }, [mobileOpen]);

  const CustomSwitch = styled(Switch)({
    '.MuiSwitch-thumb': {
      backgroundColor: '#e3e5e8',
    },
    '.MuiSwitch-track': {
      backgroundColor: uicolors.blueNav[950],
      opacity: 0.6,
    },
    '& .Mui-checked': {
      '.MuiSwitch-thumb': {
        backgroundColor: '#e3e5e8',
      },
    },
  });

  const chart = {
    chart: {
      type: 'column',
      backgroundColor: '#00000000',
      width: isMobile ? width * 0.65 : 350,
      height: width < 400 ? '300px' : null,
    },
    title: {
      text: '',
    },
    plotOptions: {
      column: {
        minPointLength: 3,
      },
    },

    xAxis: {
      type: 'category',
      lineWidth: 0,
      labels: {
        rotation: [-45],
        useHTML: true,
        formatter: function (x: { value: string }) {
          // Find the matching AgtNo
          const isAgent = data.find(
            (i) => i.AgtName === x.value && i.AgtNo === AgtNo,
          );
          return `<span style="color: ${
            isAgent ? uicolors.gold[500] : uicolors.blueNav[950]
          }; font-weight: ${isAgent ? 'bold' : 'normal'};
          font-size:${x.value.length > 15 ? '6px' : '8px'};font-family:'Lato';">
            ${x.value}
          </span>`;
        },
      },
    },
    yAxis: {
      min: 0,
      gridLineWidth: 0,
      title: {
        text: '',
      },
      labels: {
        style: {
          color: uicolors.blueNav[950],
          fontSize: width < 400 ? '8px' : '10px',
          fontWeight: 'bold',
        },
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      backgroundColor: uicolors.blueNav[950],
      borderWidth: 3,
      borderRadius: 25,
      style: {
        color: '#fff',
        display: 'flex',
      },
      // @ts-ignore
      formatter: function () {
        // @ts-ignore
        const value = this.point.y;
        // @ts-ignore
        const name = this.point.options.name;
        return `
        <div style="text-transform:none;padding:5px;display:flex;flex-direction:column;align-items:center;justify-content:center;">
        <span style="font-size:12px;">${name}</span>
        <span style="font-size:14px;">${
          isCurrency
            ? toCurrency({
                value: value,
              })
            : value
        }</span> 
        </div>`;
      },
    },
    series: [
      {
        groupPadding: 0,
        borderWidth: 0,
        borderRadius: 5,
        data: data.map((i) => {
          const isHighlighted = i.AgtNo === AgtNo; // Check if AgtNo matches
          return {
            name: i.AgtName,
            y: i.Total,
            color: isHighlighted
              ? {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, // Highlight color
                  stops: [
                    [0, uicolors.gold[100]],
                    [0.5, uicolors.gold[500]],
                    [1, uicolors.gold[900]],
                  ],
                }
              : {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, // Default color
                  stops: [
                    [0, '#00baff'],
                    [1, '#003451'],
                  ],
                },
          };
        }),
      },
    ],
    credits: {
      enabled: false,
    },
  };

  return (
    <Box
      p={1}
      sx={{
        ...homeStyles.box,
        letterSpacing: 1,
        maxWidth: isMobile ? width * 0.7 : 350,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        filter: cantView ? 'blur(10px)' : '',
        pointerEvents: cantView ? 'none' : 'auto',
        userSelect: cantView ? 'none' : 'auto',
        position: 'relative',
        border: `1px solid ${grey[300]}`,
        backgroundColor: '#fff',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            color: uicolors.gold[500],
            fontWeight: 'bold',
            fontSize: 16,
            letterSpacing: 1.1,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {Title ? Title : <Skeleton width="100%" />}

          {props.ToolTip ? (
            <Box sx={{ position: 'absolute', top: 2, right: 2 }}>
              <Tooltip
                title={props.ToolTip || ''}
                arrow
                placement="bottom"
                slotProps={{
                  tooltip: { sx: { backgroundColor: '#17191B', p: 1 } },
                  arrow: { sx: { color: '#17191B' } },
                }}
              >
                <InfoOutlined
                  fontSize="small"
                  sx={{
                    transform: 'scale(.8)',
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            </Box>
          ) : null}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <SignalCellularAlt
            sx={{ color: uicolors.blueNav[950], transform: 'scaleX(-1)' }}
          />
          <Tooltip
            title={
              toggle ? 'Switch to Graph View' : 'Switch to Leaderboard View'
            }
            placement="bottom"
            arrow
            slotProps={{
              tooltip: { sx: { backgroundColor: '#17191B', p: 1 } },
              arrow: { sx: { color: '#17191B' } },
            }}
          >
            <CustomSwitch
              size="small"
              color="default"
              value={toggle}
              checked={toggle}
              onChange={() => setToggle(!toggle)}
            />
          </Tooltip>
          <FormatListNumbered sx={{ color: uicolors.blueNav[950] }} />
        </Box>
      </Box>

      {!toggle ? (
        data.length > 0 ? (
          <HighchartsReact highcharts={Highcharts} options={chart} />
        ) : (
          <Skeleton variant="rectangular">
            <HighchartsReact highcharts={Highcharts} options={chart} />
          </Skeleton>
        )
      ) : (
        <AnimateIn to="top" nofade="true" pixels={50}>
          <Stack
            sx={{
              width: isMobile ? width * 0.65 : 350,
              py: 1,
            }}
          >
            {data.map((i, index) => {
              return (
                <Box
                  key={i.AgtName}
                  sx={{
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color:
                      i.AgtNo === AgtNo
                        ? uicolors.gold[500]
                        : uicolors.blueNav[950],
                    fontWeight: i.AgtNo === AgtNo ? 700 : 400,
                    backgroundColor:
                      index % 2 ? uicolors.blue[50] : uicolors.blue[100],
                    py: 2,
                    fontSize: isMobile ? 10 : 14,
                  }}
                >
                  <Box
                    flex={0.5}
                    sx={{ textAlign: 'left', fontWeight: 'bold' }}
                  >
                    {index < 5 ? index + 1 + '.' : '\u2014'}
                  </Box>
                  <Box
                    flex={2}
                    px={1}
                    sx={{
                      textAlign: 'center',
                      textTransform: 'none',
                      fontSize: i.AgtName && i.AgtName.length > 15 ? 12 : 14,
                    }}
                  >
                    {i.AgtName}
                  </Box>

                  <Box flex={0.5} sx={{ textAlign: 'right' }}>
                    {Math.floor(i.Total || 0).toLocaleString()}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </AnimateIn>
      )}
    </Box>
  );
}
